import React from "react";
import PropTypes from "prop-types";
import {
  Dispatcher,
  NavigatorStore,
  NavigatorAction,
  BookStore,
  BookUtils
} from "../../../flux";
import { Isvg, Spinner } from "../../../components";

import Router from "../router";
import Header from "../header";
import BookReader from "../book-reader/book-reader";
import Reading from "./reading";
import ReadingOverview from "./reading-overview";
import ReadingComplete from "./reading-complete";

class AssignmentReaderChrome extends React.Component {
  constructor(props) {
    super(props);
    const state = {
      status: "opened",
      book: {},
      route: null,
      assignment: {
        vbid: this.props.isbn
      },
      readings: [],
      feenyReady: false,
      currentReading: null,
      toCancel: [],
      loading: false,
      verifyingLicense: false,
      pageLoaded: false
    };
    this.state = Object.assign(state, this.props.savedState);
  }

  componentWillUnmount() {
    this.state.toCancel.forEach(cancel => cancel());
    Dispatcher.unbind(
      Dispatcher.BOOK_NAVIGATION_REQUESTED,
      "assignments-reader-book-navigation-requested"
    );
    Dispatcher.unbind(
      Dispatcher.BOOK_PAGE_LOAD,
      "assignments-reader-book-page-load"
    );
  }

  componentDidMount() {
    document.title += `: ${i18n.t("assignments.preview")}`;
    Dispatcher.emit(Dispatcher.MODAL_OPEN);
    this.setState({ verifyingLicense: true });
    Jigsaw.getBookInfo(this.props.isbn, response => {
      this.setState({ verifyingLicense: false });
      if (response.code === 200 && response.data && response.data.hasLicense) {
        if (!this.props.savedState) {
          this.initFeeny();
        }
      } else {
        if (response.code !== 401) {
          if (Fraction.User && Fraction.User.sessionId) {
            Fraction.Router.setRoute(`/?unavailable=${this.props.isbn}`);
          }
        }
      }
    });

    Dispatcher.bind(
      Dispatcher.BOOK_NAVIGATION_REQUESTED,
      () => {
        this.setState({ pageLoaded: false });
      },
      "assignments-reader-book-navigation-requested"
    );

    Dispatcher.bind(
      Dispatcher.BOOK_PAGE_LOAD,
      () => {
        this.setState({ pageLoaded: true });
      },
      "assignments-reader-book-page-load"
    );
  }

  initFeeny() {
    this.setState({ loading: true });
    const feenyInit = Feeny.init({ cancelable: true });
    const toCancel = this.state.toCancel;
    toCancel.push(feenyInit.cancel.bind(feenyInit));
    this.setState(
      {
        toCancel,
        status: "opened",
        loading: true,
        book: BookStore.get(this.props.isbn)
      },
      this.saveState
    );

    feenyInit.promise
      .then(() => {
        this.setState({ feenyReady: true });
        this.loadAssignment();
      })
      .catch(() => this._close());
  }

  loadAssignment() {
    const assignmentId = this.props.assignmentId;
    if (assignmentId) {
      Feeny.UserAssignments.create(assignmentId) // find or create
        .then(result => {
          this.setState({ assignment: result.data });
          this.loadReadings();
          Fraction.Reporter.gaEvent("assignments/reader/loaded", assignmentId);
        })
        .catch(error => {
          Fraction.Helpers.toast(i18n.t("assignments.invalid-assignment"));
          this._close();
        });
    }
  }

  loadReadings() {
    const assignmentId = this.props.assignmentId;
    if (assignmentId) {
      Feeny.UserReadings.all(assignmentId)
        .then(response => {
          const currentReading = response.data.find(
            r => r.uuid === this.props.readingId
          );
          this.setState(
            {
              readings: response.data,
              loading: false,
              currentReading
            },
            () => {
              const reading = this.state.currentReading;
              if (reading) {
                NavigatorAction.replace("assignment-reading", {
                  reading
                });
              }
            }
          );
        })
        .catch(error => {
          Fraction.Helpers.toast(i18n.t("assignments.invalid-assignment"));
          this._close();
        });
    }
  }

  saveState(state) {
    if (typeof this.props.saveState === "function") {
      if (state) {
        this.props.saveState(Object.assign(this.state, state));
      } else {
        this.props.saveState(this.state);
      }
    }
  }

  clearState() {
    if (typeof this.props.saveState === "function") {
      this.props.saveState(false);
    }
  }

  _minimize(state, callback) {
    state = state || {};
    state.status = "minimized";
    this.saveState(state);
    Dispatcher.emit(Dispatcher.CHANGE_CHROME, "", null, callback);
  }

  _close() {
    NavigatorAction.reset();
    this.clearState();
    Fraction.Helpers.clearRedirect();
    Fraction.Helpers.redirectMe();
  }

  _markReadingAsComplete(readingId) {
    const readings = this.state.readings;
    if (readings.length) {
      this.setState({ loading: true });
      readings.forEach(reading => {
        if (reading.uuid === readingId && !reading.completed_at) {
          Feeny.UserReadings.update(this.state.assignment.uuid, reading.uuid, {
            completion: 100
          })
            .then(() => {
              this.loadReadings();
              Fraction.Reporter.gaEvent(
                "assignments/reader/reading-complete",
                readingId
              );
            })
            .catch(error => this.setState({ loading: false }));
        }
      });

      if (
        !readings.some(
          reading => reading.uuid === readingId && !reading.completed_at
        )
      ) {
        this.setState({ loading: false });
      }
    }
  }

  render() {
    if (this.state.loading || this.state.verifyingLicense) {
      return <Spinner />;
    }
    return (
      <div id="assignments">
        {!this.state.pageLoaded && (
          <div id="invisible-loader-for-selenium-tests" />
        )}
        <Router>
          <ReadingOverview
            route="reading-overview"
            isbn={this.props.isbn}
            book={this.state.book}
            readings={this.state.readings}
            assignment={this.state.assignment}
            minimize={this._minimize.bind(this)}
            close={this._close.bind(this)}
          />

          <Reading
            route="assignment-reading"
            isbn={this.props.isbn}
            book={this.state.book}
            reading={this.state.currentReading}
            readings={this.state.readings}
            assignment={this.state.assignment}
            markReadingAsComplete={this._markReadingAsComplete.bind(this)}
            minimize={this._minimize.bind(this)}
            close={this._close.bind(this)}
            savedState={this.state["assignment-reading"]}
          >
            {this.props.children}
          </Reading>

          <ReadingComplete
            route="reading-complete"
            isbn={this.props.isbn}
            book={this.state.book}
            reading={this.state.currentReading}
            readings={this.state.readings}
            assignment={this.state.assignment}
            minimize={this._minimize.bind(this)}
            close={this._close.bind(this)}
          />
        </Router>
      </div>
    );
  }
}

AssignmentReaderChrome.propTypes = {
  isbn: PropTypes.string.isRequired,
  assignmentId: PropTypes.string.isRequired,
  readings: PropTypes.array,
  assignment: PropTypes.object
};

module.exports = {
  class: AssignmentReaderChrome,
  metadata: () => {
    return {
      id: "assignment-reader-chrome",
      name: i18n.t("assignments.assignment-creator"),
      description: i18n.t("assignments.description"),
      type: "chrome"
    };
  }
};
